import {Metadata} from "../models/remoteConstants.model";
import {AttributeParameterManifest, AttributeEntityManifest, FieldConstraint} from "../models/attributeManifest.model";
import {useApp} from "../app.context";
import {useIntegrator} from "../integrator.context";
import {
    MetadataCollection,
    MetadataValueMethod,
    MetadataValueCommand,
    MetadataValueTypes,
    isAttributeCollection
} from "../commands/metadataValue.command";
import NumberInputLabel from "./numberInputLabel.element";
import AssetImage from "./assetImage.element";
import SelectableLabel from "./selectable.label";
import BooleanInput from "./booleanInput.element";
import {ChangeMetadataCommand} from "../commands/changeMetadata.command";
import {getParameterSuffix, resolveEntityRef} from "../utils/metadata.utils";
import {navTargetProps} from "./useNavTarget";
import {NavTarget} from "../utils/navTarget";
import {useEffect, useRef, useState} from "react";
import ImagePicker from "../features/general/imagePicker.view";
import {Tooltip} from "@mui/material";

interface MetadataProps {
    metadata: Metadata;
    collection: MetadataCollection;
    onDelete(): void;
}

// Human-readable category label for the confirm-delete dialog, derived from
// which metadata collection the entry belongs to.
function collectionCategory(c: MetadataCollection): string {
    switch (c) {
        case MetadataCollection.Units: return 'unit attribute';
        case MetadataCollection.Buildings: return 'building attribute';
        case MetadataCollection.Rewards: return 'reward';
        case MetadataCollection.TechActions: return 'tech action';
        case MetadataCollection.TechPassives: return 'tech passive';
        default: return 'metadata';
    }
}

function MetadataElement(props: MetadataProps) {
    const {state, executeCommand} = useIntegrator();
    const [showIconPicker, setShowIconPicker] = useState(false);
    const {setNotification, setMetadataValue, getEntityOptions, getAttributeManifestEntry, getEntityOptionsForKind, getLocalizationValue, getTypeDescriptionKey, confirmDelete, navigateToUnit, navigateToBuilding, navigateTo, focusedMetadata, showIds} = useApp();

    // Ctrl/Cmd-click navigation target: scroll this row into view + briefly
    // highlight it when it becomes the focused metadata. These rows are always
    // expanded, so the highlight is the "you arrived here" cue.
    const rowRef = useRef<HTMLDivElement | null>(null);
    const [highlight, setHighlight] = useState(false);
    useEffect(() => {
        if (focusedMetadata?.uid === props.metadata.uid) {
            rowRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
            setHighlight(true);
            const t = setTimeout(() => setHighlight(false), 450);
            return () => clearTimeout(t);
        }
    }, [focusedMetadata, props.metadata.uid]);

    const valueKeys = Object.keys(props.metadata.values);
    const entityKeys = Object.keys(props.metadata.entities);
    const requires = props.metadata.requires ?? [];
    // Metadata array for this collection — used to resolve a "Requires" entry
    // (a same-collection attribute *id*) to its uid for Ctrl/Cmd-click nav.
    const collectionArray =
          props.collection === MetadataCollection.Units ? state.unitAttributes
        : props.collection === MetadataCollection.Buildings ? state.buildingAttributes
        : props.collection === MetadataCollection.TechActions ? state.techActionAttributes
        : props.collection === MetadataCollection.TechPassives ? state.techPassiveAttributes
        : state.rewards;
    const requireTarget = (req: string): NavTarget | undefined => {
        const m = collectionArray?.find(x => x.id === req);
        return m ? {kind: 'metadata', uid: m.uid} : undefined;
    };

    // Number and EntityValue params share `values`; distinguish at render time
    // by checking whether the key matches a known entity uid. If it does, this
    // is an EntityValue entry — localize so the label reads "City" not the
    // raw uid. Otherwise it's a user-named Number param — render literally.
    const entityIds = new Set<string>([
        ...state.units.map(u => u.id),
        ...state.buildings.map(b => b.id),
        ...state.tiles.map(t => t.id),
        ...state.resources.map(r => r.id),
    ]);
    const titleForValueKey = (key: string) =>
        entityIds.has(key) ? getLocalizationValue(key) : key;
    // uid for instance name, id for shared description template
    const locKey = props.metadata.uid;

    // "Assigned to" — for Unit and Building attribute collections, list every
    // entity whose `attributes` array references this metadata uid. Clicking an
    // entry navigates to that entity in its tab and opens its collapsible.
    const isUnitAttr = props.collection === MetadataCollection.Units;
    const isBuildingAttr = props.collection === MetadataCollection.Buildings;
    const isReward = props.collection === MetadataCollection.Rewards;
    const isTechPassive = props.collection === MetadataCollection.TechPassives;
    const isTechAction = props.collection === MetadataCollection.TechActions;
    const assignedEntities: { id: string }[] = isUnitAttr
        ? state.units.filter(u => u.attributes?.includes(props.metadata.uid))
        : isBuildingAttr
            ? state.buildings.filter(b => b.attributes?.includes(props.metadata.uid))
            : [];

    // The manifest entry (if any) for this attribute id. When present it drives
    // a typed, labeled form; when absent (legacy attribute, or a manifest
    // predating the collection's section) we fall back to free-form editing.
    const manifestEntry = (isUnitAttr || isBuildingAttr || isReward || isTechPassive || isTechAction)
        ? getAttributeManifestEntry(props.metadata.id, props.collection)
        : undefined;
    // Hidden entries (Unit Types) are type markers — they're auto-seeded from
    // the manifest, can't have parameters (enforced at export time), and
    // shouldn't be deleted (they'd just reappear) or renamed (the id is fixed
    // to the manifest entry). We suppress the corresponding affordances below.
    const isHidden = manifestEntry?.Hidden === true;
    const paramNames = new Set(manifestEntry?.Parameters.map(p => p.Name) ?? []);
    const entityNames = new Set(manifestEntry?.Entities.map(e => e.Name) ?? []);
    // Authored keys the manifest doesn't know about (renamed/removed params or
    // pre-manifest data). Surfaced separately so nothing is hidden or dropped.
    const unknownValueKeys = valueKeys.filter(k => !paramNames.has(k));
    const unknownEntityKeys = entityKeys.filter(k => !entityNames.has(k));

    // A plain numeric value row (free-form key) — used for the fallback path and
    // for unrecognized keys under a manifest-backed attribute.
    const renderValueRow = (key: string, index: number) => {
        const value = props.metadata.values[key];
        return (
            <div key={`v-${index}`} {...navTargetProps(navigateTo, entityIds.has(key) ? resolveEntityRef(state, key) : undefined)} className={'layout horizontal gap stretch'} style={{width: '100%'}}>
                <NumberInputLabel title={titleForValueKey(key)} value={value} onChange={(v) => {
                    executeCommand(new MetadataValueCommand(props.collection, MetadataValueMethod.AddOrUpdate, MetadataValueTypes.Number, props.metadata.uid, key, v));
                }}/>
                <button onClick={async () => {
                    if (await confirmDelete(key, 'parameter'))
                        executeCommand(new MetadataValueCommand(props.collection, MetadataValueMethod.Remove, MetadataValueTypes.Number, props.metadata.uid, key));
                }}>
                    <span className={'fa-solid fa-trash'}/>
                </button>
            </div>
        );
    };

    const renderEntityRow = (key: string, index: number) => {
        const value = props.metadata.entities[key];
        return (
            <div key={`e-${index}`} {...navTargetProps(navigateTo, resolveEntityRef(state, value))} className={'layout horizontal gap'} style={{width: '100%'}}>
                <SelectableLabel name={key} value={value} groups={getEntityOptions()} onChange={(v) => {
                    executeCommand(new MetadataValueCommand(props.collection, MetadataValueMethod.AddOrUpdate, MetadataValueTypes.Entity, props.metadata.uid, key, v));
                }} style={{width: '100%'}}/>
                <button onClick={async () => {
                    if (await confirmDelete(key, 'parameter'))
                        executeCommand(new MetadataValueCommand(props.collection, MetadataValueMethod.Remove, MetadataValueTypes.Entity, props.metadata.uid, key));
                }}>
                    <span className={'fa-solid fa-trash'}/>
                </button>
            </div>
        );
    };

    // A manifest parameter row: labeled by Name, defaulted from the manifest,
    // writing to values[Name]. The values dict is numeric, so params render as
    // number inputs (step 1 for ints). The reset button clears the override,
    // reverting to the manifest default.
    const renderManifestParam = (param: AttributeParameterManifest, index: number) => {
        const authored = props.metadata.values[param.Name];
        const def = Number(param.DefaultValue);
        const isOverridden = authored !== undefined;

        // Clamp the input only when the manifest declares a range for this param
        // (Type carried too, so int params round on commit). Unannotated params
        // stay exactly as before.
        const paramConstraint: FieldConstraint | undefined =
            (param.Min != null || param.Max != null || param.Step != null)
                ? {Type: param.Type, Min: param.Min, Max: param.Max, Step: param.Step}
                : undefined;

        // Label with an optional tooltip when the manifest carries a per-parameter
        // description (used by the custom-layout branches; the numeric branches
        // pass param.Comment straight to NumberInputLabel's tooltip prop).
        const labelEl = (text: string) => param.Comment
            ? <Tooltip title={param.Comment} arrow disableInteractive>
                <p className={'flex-grow'} style={{cursor: 'help'}}>{text}</p>
              </Tooltip>
            : <p className={'flex-grow'}>{text}</p>;

        // Boolean params are stored as 1/0 in the numeric `values` dict; render a
        // checkbox rather than a number input. Reset removes the key (→ default).
        if (param.Type === 'bool') {
            const current = (authored ?? def) !== 0;
            return (
                <div key={`p-${index}`} className={'layout horizontal gap stretch'} style={{width: '100%', alignItems: 'center'}}>
                    {labelEl(param.Name)}
                    <BooleanInput value={current} onChange={(v) => {
                        executeCommand(new MetadataValueCommand(props.collection, MetadataValueMethod.AddOrUpdate, MetadataValueTypes.Number, props.metadata.uid, param.Name, v ? 1 : 0));
                    }}/>
                    <button disabled={!isOverridden} style={{opacity: isOverridden ? 1 : 0.4}}
                            title={isOverridden ? `Reset to default (${!!def})` : `Already default (${!!def})`}
                            onClick={() => {
                        executeCommand(new MetadataValueCommand(props.collection, MetadataValueMethod.Remove, MetadataValueTypes.Number, props.metadata.uid, param.Name));
                    }}>
                        <span className={'fa-solid fa-rotate-left'}/>
                    </button>
                </div>
            );
        }

        // Nullable params (e.g. float?) treat "key absent" as a meaningful null
        // the game branches on — so render them as optional: "Off" with an enable
        // button until set, then a number input with a clear button that removes
        // the key (back to null). No misleading numeric default is shown.
        if (param.Nullable) {
            if (!isOverridden) {
                return (
                    <div key={`p-${index}`} className={'layout horizontal stretch'} style={{width: '100%', alignItems: 'center', gap: 10}}>
                        {labelEl(param.Name)}
                        <p className={'muted-text'} style={{fontSize: 13}}>Off</p>
                        <button title={'Enable (set a value)'} onClick={() => {
                            executeCommand(new MetadataValueCommand(props.collection, MetadataValueMethod.AddOrUpdate, MetadataValueTypes.Number, props.metadata.uid, param.Name, 0));
                        }}>
                            <span className={'fa-solid fa-plus'}/>
                        </button>
                    </div>
                );
            }
            return (
                <div key={`p-${index}`} className={'layout horizontal stretch'} style={{width: '100%'}}>
                    <NumberInputLabel
                        title={param.Name}
                        tooltip={param.Comment}
                        value={authored ?? 0}
                        step={param.Type === 'int' ? '1' : 'any'}
                        constraint={paramConstraint}
                        inputStyle={{borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 'none'}}
                        onChange={(v) => {
                            executeCommand(new MetadataValueCommand(props.collection, MetadataValueMethod.AddOrUpdate, MetadataValueTypes.Number, props.metadata.uid, param.Name, v));
                        }}/>
                    <button style={{backgroundColor: 'transparent', border: 'solid 1px var(--overlay-color)', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, padding: '0 12px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                            title={'Clear (off)'}
                            onClick={() => {
                        executeCommand(new MetadataValueCommand(props.collection, MetadataValueMethod.Remove, MetadataValueTypes.Number, props.metadata.uid, param.Name));
                    }}>
                        <span className={'fa-solid fa-rotate-left'}/>
                    </button>
                </div>
            );
        }

        // Enum params store the member's integer in `values`; render a labelled
        // dropdown of the member names (EnumValues[int]) instead of a raw number.
        if (param.Type === 'enum' && param.EnumValues && param.EnumValues.length > 0) {
            const names = param.EnumValues;
            const current = authored ?? def;
            const currentName = names[current] ?? names[0];
            const defName = names[def] ?? String(def);
            return (
                // key includes current so the uncontrolled <select> remounts and
                // reflects a reset-to-default.
                <div key={`p-${index}-${currentName}`} className={'layout horizontal gap stretch'} style={{width: '100%'}}>
                    <SelectableLabel name={param.Name} tooltip={param.Comment} value={currentName} options={names}
                        style={{width: '100%'}}
                        onChange={(v) => {
                            const idx = names.indexOf(v as string);
                            executeCommand(new MetadataValueCommand(props.collection, MetadataValueMethod.AddOrUpdate, MetadataValueTypes.Number, props.metadata.uid, param.Name, idx));
                        }}/>
                    <button disabled={!isOverridden} style={{opacity: isOverridden ? 1 : 0.4}}
                            title={isOverridden ? `Reset to default (${defName})` : `Already default (${defName})`}
                            onClick={() => {
                        executeCommand(new MetadataValueCommand(props.collection, MetadataValueMethod.Remove, MetadataValueTypes.Number, props.metadata.uid, param.Name));
                    }}>
                        <span className={'fa-solid fa-rotate-left'}/>
                    </button>
                </div>
            );
        }
        return (
            <div key={`p-${index}`} className={'layout horizontal stretch'} style={{width: '100%'}}>
                <NumberInputLabel
                    title={param.Name}
                    tooltip={param.Comment}
                    value={authored ?? def}
                    step={param.Type === 'int' ? '1' : 'any'}
                    constraint={paramConstraint}
                    inputStyle={{borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 'none'}}
                    onChange={(v) => {
                        executeCommand(new MetadataValueCommand(props.collection, MetadataValueMethod.AddOrUpdate, MetadataValueTypes.Number, props.metadata.uid, param.Name, v));
                    }}/>
                <button disabled={!isOverridden}
                        style={{backgroundColor: 'transparent', border: 'solid 1px var(--overlay-color)', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, padding: '0 12px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                        title={isOverridden ? `Reset to default (${def})` : `Already default (${def})`}
                        onClick={() => {
                    executeCommand(new MetadataValueCommand(props.collection, MetadataValueMethod.Remove, MetadataValueTypes.Number, props.metadata.uid, param.Name));
                }}>
                    <span className={'fa-solid fa-rotate-left'} style={{opacity: isOverridden ? 1 : 0.4}}/>
                </button>
            </div>
        );
    };

    // A manifest entity row: labeled by Name, picker scoped to the declared
    // Kind, writing to entities[Name]. The clear button removes it.
    const renderManifestEntity = (entity: AttributeEntityManifest, index: number) => {
        const value = props.metadata.entities[entity.Name] ?? '';
        const isSet = entity.Name in props.metadata.entities;
        return (
            <div key={`me-${index}`} {...navTargetProps(navigateTo, value ? resolveEntityRef(state, value) : undefined)} className={'layout horizontal gap'} style={{width: '100%'}}>
                <SelectableLabel name={entity.Name} tooltip={entity.Comment} value={value} groups={getEntityOptionsForKind(entity.Kind)}
                    placeholder={'Select…'}
                    onChange={(v) => {
                    executeCommand(new MetadataValueCommand(props.collection, MetadataValueMethod.AddOrUpdate, MetadataValueTypes.Entity, props.metadata.uid, entity.Name, v));
                }} style={{width: '100%'}}/>
                {isSet && <button title={'Clear'} onClick={() => {
                    executeCommand(new MetadataValueCommand(props.collection, MetadataValueMethod.Remove, MetadataValueTypes.Entity, props.metadata.uid, entity.Name));
                }}>
                    <span className={'fa-solid fa-rotate-left'}/>
                </button>}
            </div>
        );
    };

    return (
        <div ref={rowRef} className={'layout vertical center stretch gap box transition'} style={{padding: '10px', borderRadius: 'var(--border-radius)', backgroundColor: 'var(--box-color)', opacity: props.metadata.active ? '1' : '0.5', borderColor: highlight ? 'var(--tint-color)' : undefined}}>
            <div className={'layout horizontal center stretch'} style={{width: '100%'}}>
                <div className={'layout horizontal gap center'} style={{gap: 10}}>
                    {/* Attribute collections carry no icon (see
                        isAttributeCollection); Rewards and Tech keep the slot. */}
                    {!isAttributeCollection(props.collection) && <>
                        <AssetImage id={props.metadata.uid} size={40}
                                    style={{cursor: 'pointer'}}
                                    onClick={() => setShowIconPicker(true)}/>
                        {showIconPicker &&
                            <ImagePicker target={{kind: 'metadataIcon', uid: props.metadata.uid}}
                                         currentKey={state.imagePacks?.[props.metadata.uid]?.[0]?.[0]
                                             ?? (state.images && props.metadata.uid in state.images ? props.metadata.uid : undefined)}
                                         onClose={() => setShowIconPicker(false)}/>}
                    </>}
                    <div className={'layout vertical'}>
                        <div className={'layout horizontal gap'}>
                            <BooleanInput value={props.metadata.active} onChange={(value) => {
                                props.metadata.active = value;
                                executeCommand(new ChangeMetadataCommand(props.metadata.uid, props.collection, metadata => {
                                    metadata.active = value;
                                }));
                            }}/>
                            <Tooltip title={manifestEntry?.Comment ?? ''} arrow disableInteractive>
                                <p style={{width: 160}} onClick={() => setNotification({
                                    title: locKey,
                                    message: getTypeDescriptionKey(props.metadata.uid, props.metadata.id),
                                    edit: true
                                })}>
                                    {getLocalizationValue(locKey)}{isReward ? getParameterSuffix(props.metadata, manifestEntry, getLocalizationValue) : ''}
                                </p>
                            </Tooltip>
                        </div>
                        {showIds && <div className={'layout horizontal gap'}>
                            <p style={{color: 'var(--muted-text-color)'}}>ID:</p>
                            <p style={{fontSize: 15, color: 'var(--muted-text-color)'}}>{props.metadata.id}</p>
                        </div>}
                    </div>
                </div>
                <div className={'layout horizontal gap'}>
                    {!isHidden && <button onClick={(e) => {
                        e.stopPropagation();
                        setMetadataValue({
                            metadata: props.metadata,
                            collection: props.collection
                        });
                    }}>
                        <span className={'fa-solid fa-add'}/>
                    </button>}
                    {!isHidden && <button onClick={async (e) => {
                        e.stopPropagation();
                        if (await confirmDelete(getLocalizationValue(props.metadata.id), collectionCategory(props.collection)))
                            props.onDelete();
                    }}>
                        <span className={'fa-solid fa-trash'}/>
                    </button>}
                </div>
            </div>
            {manifestEntry ? <>
                {manifestEntry.Parameters.map((param, index) => {
                    // A param can declare it's superseded by another (e.g. price by
                    // percentPrice): while that one is authored the game ignores this
                    // one, so grey it out + block input as a "can't use both" cue.
                    const superseded = param.SupersededBy != null
                        && props.metadata.values[param.SupersededBy] !== undefined;
                    const row = renderManifestParam(param, index);
                    if (!superseded) return row;
                    return (
                        <div key={`p-${index}`} className={'layout vertical'}
                             style={{width: '100%', opacity: 0.45, pointerEvents: 'none'}}>
                            {row}
                            <p className={'muted-text'} style={{fontSize: 12}}>
                                Overridden by “{param.SupersededBy}”
                            </p>
                        </div>
                    );
                })}
                {manifestEntry.Entities.map(renderManifestEntity)}
                {(unknownValueKeys.length > 0 || unknownEntityKeys.length > 0) && <>
                    <div className={'layout horizontal center gap'} style={{width: '100%'}}>
                        <span className={'fa-solid fa-triangle-exclamation'} style={{color: 'orange'}}/>
                        <p style={{color: 'var(--muted-text-color)', fontSize: 13}}>
                            Unrecognized (not in manifest)
                        </p>
                    </div>
                    {unknownValueKeys.map(renderValueRow)}
                    {unknownEntityKeys.map(renderEntityRow)}
                </>}
            </> : <>
                {valueKeys.map(renderValueRow)}
                {entityKeys.map(renderEntityRow)}
            </>}
            {requires.length > 0 && <>
                {requires.map((req, index) => (
                    <div key={index} className={'layout horizontal gap stretch'} style={{width: '100%'}}>
                        <div className={'layout horizontal center gap'} {...navTargetProps(navigateTo, requireTarget(req))} style={{flexGrow: 1}}>
                            <p style={{color: 'var(--muted-text-color)'}}>Requires:</p>
                            <p>{req}</p>
                        </div>
                        <button onClick={async () => {
                            if (await confirmDelete(req, 'requirement'))
                                executeCommand(new MetadataValueCommand(props.collection, MetadataValueMethod.Remove, MetadataValueTypes.Requires, props.metadata.uid, req));
                        }}>
                            <span className={'fa-solid fa-trash'}/>
                        </button>
                    </div>
                ))}
            </>}
            {(isUnitAttr || isBuildingAttr) && (
                <div className={'layout vertical gap'} style={{width: '100%'}}>
                    <p style={{color: 'var(--muted-text-color)', fontSize: 13}}>
                        Assigned to ({assignedEntities.length})
                    </p>
                    {assignedEntities.length === 0
                        ? <p className={'muted-text'} style={{fontSize: 13}}>None</p>
                        : assignedEntities.map((entity) => (
                            <p key={entity.id} style={{fontSize: 13, width: '100%'}}>
                                <span onClick={() => isUnitAttr ? navigateToUnit(entity.id) : navigateToBuilding(entity.id)}
                                      style={{
                                          cursor: 'pointer',
                                          color: 'var(--tint-color)',
                                          textDecoration: 'underline',
                                      }}>
                                    {getLocalizationValue(entity.id)}
                                </span>
                            </p>
                        ))
                    }
                </div>
            )}
        </div>
    )
}

export default MetadataElement;